import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Bell, Mail, Search, Plus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface SavedSearchToggleProps {
  searchQuery: string;
  filters: {
    location?: string;
    job_type?: string;
    experience_level?: string;
    is_remote?: boolean;
    salary_min?: number;
  };
  resultCount: number;
}

export const SavedSearchToggle: React.FC<SavedSearchToggleProps> = ({
  searchQuery,
  filters,
  resultCount
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [frequency, setFrequency] = useState('daily');
  const [alertTitle, setAlertTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingAlert, setExistingAlert] = useState<any>(null);

  useEffect(() => {
    if (user && searchQuery.trim()) {
      generateAlertTitle();
      checkExistingAlert();
    }
  }, [user, searchQuery, filters]);

  const generateAlertTitle = () => {
    const parts = [];
    if (searchQuery.trim()) parts.push(searchQuery.trim());
    if (filters.location && filters.location !== 'all') parts.push(`in ${filters.location}`);
    if (filters.job_type && filters.job_type !== 'all') parts.push(filters.job_type);
    if (filters.experience_level && filters.experience_level !== 'all') parts.push(filters.experience_level);
    if (filters.is_remote) parts.push('Remote');
    
    const title = parts.join(' ') || 'Job Search';
    setAlertTitle(title.length > 50 ? title.substring(0, 47) + '...' : title);
  };

  const checkExistingAlert = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('job_alerts')
        .select('*')
        .eq('user_id', user.id)
        .eq('title', alertTitle)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking existing alert:', error);
        return;
      }

      if (data) {
        setExistingAlert(data);
        setEmailAlerts(data.is_active);
        setFrequency(data.frequency);
      }
    } catch (error) {
      console.error('Error checking existing alert:', error);
    }
  };

  const handleSaveSearch = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save searches and set up email alerts.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const alertData = {
        user_id: user.id,
        title: alertTitle,
        keywords: searchQuery ? [searchQuery.trim()] : [],
        job_types: filters.job_type && filters.job_type !== 'all' ? [filters.job_type] : [],
        experience_levels: filters.experience_level && filters.experience_level !== 'all' ? [filters.experience_level] : [],
        locations: filters.location && filters.location !== 'all' ? [filters.location] : [],
        is_remote: filters.is_remote || false,
        salary_min: filters.salary_min || null,
        salary_max: null,
        currency: 'LKR',
        frequency,
        is_active: emailAlerts,
      };

      if (existingAlert) {
        const { error } = await supabase
          .from('job_alerts')
          .update(alertData)
          .eq('id', existingAlert.id);
        
        if (error) throw error;
        toast({
          title: "Search updated",
          description: "Your saved search has been updated successfully.",
        });
      } else {
        const { error } = await supabase
          .from('job_alerts')
          .insert([alertData]);
        
        if (error) throw error;
        toast({
          title: "Search saved",
          description: "You'll receive email alerts for new matching jobs.",
        });
      }

      setIsExpanded(false);
      checkExistingAlert();
    } catch (error) {
      console.error('Error saving search:', error);
      toast({
        title: "Error",
        description: "Failed to save search. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || !searchQuery.trim()) return null;

  if (!isExpanded) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Found {resultCount} jobs</span>
              </div>
              {existingAlert && (
                <Badge variant="secondary" className="text-xs">
                  <Bell className="w-3 h-3 mr-1" />
                  Alert Active
                </Badge>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="text-primary border-primary/20 hover:bg-primary/10"
            >
              <Mail className="w-4 h-4 mr-2" />
              {existingAlert ? 'Update Alert' : 'Email me similar jobs'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-4 h-4" />
          {existingAlert ? 'Update Job Alert' : 'Save Search & Get Email Alerts'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="alert-title" className="text-sm font-medium">
            Alert Name
          </Label>
          <Input
            id="alert-title"
            value={alertTitle}
            onChange={(e) => setAlertTitle(e.target.value)}
            placeholder="Enter a name for this alert"
            className="mt-1"
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="email-alerts" className="text-sm font-medium">
            Email notifications
          </Label>
          <Switch
            id="email-alerts"
            checked={emailAlerts}
            onCheckedChange={setEmailAlerts}
          />
        </div>

        {emailAlerts && (
          <div>
            <Label htmlFor="frequency" className="text-sm font-medium">
              Email frequency
            </Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">Instant</SelectItem>
                <SelectItem value="daily">Daily digest</SelectItem>
                <SelectItem value="weekly">Weekly summary</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>Search criteria:</strong></p>
            <p>• Keywords: {searchQuery || 'Any'}</p>
            {filters.location && filters.location !== 'all' && (
              <p>• Location: {filters.location}</p>
            )}
            {filters.job_type && filters.job_type !== 'all' && (
              <p>• Job Type: {filters.job_type}</p>
            )}
            {filters.experience_level && filters.experience_level !== 'all' && (
              <p>• Experience: {filters.experience_level}</p>
            )}
            {filters.is_remote && <p>• Remote work: Yes</p>}
            {filters.salary_min && (
              <p>• Minimum salary: LKR {filters.salary_min.toLocaleString()}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSaveSearch}
            disabled={loading || !alertTitle.trim()}
            className="flex-1"
          >
            {loading ? (
              "Saving..."
            ) : existingAlert ? (
              "Update Alert"
            ) : (
              "Save & Create Alert"
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsExpanded(false)}
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SavedSearchToggle;